import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Search, Heart, ArrowRight, Sparkles, PenTool, FileText, Package, Plus, Info } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { libApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CATEGORY_ICON_MAP, STATUS_ICONS } from "@/components/app/CategoryIcons";
import { LIBRARY } from "@/constants/testIds";

const FEATURED_COUNT = 6;
const CATEGORY_LIMIT = 6;
const PAGE_SIZE = 12;

const SEARCH_PLACEHOLDER = {
  "nieruchomosci": "Np. mieszkanie na wynajem, rolka z prezentacji domu…",
  "beauty-kosmetyka-salony": "Np. metamorfoza, opis zabiegu, post z promocją…",
  "budownictwo-uslugi-lokalne": "Np. wycena remontu, post z realizacji, odpowiedź dla klienta…",
  "produkty-e-commerce": "Np. zdjęcie produktu, opis do karty, reklama na promocję…",
  "postacie-ai-influencerzy": "Np. portret postaci, bio na profil, scenariusz UGC…",
  "marka-osobista-eksperci": "Np. bio eksperta, post edukacyjny, oferta konsultacji…",
  "zwierzeta-branza-zoologiczna": "Np. ogłoszenie o miocie, opis do adopcji, post sklepu zoo…",
  "gastronomia": "Np. opis dania do menu, zdjęcie potrawy, post z promocją lunchu…",
};
const DEFAULT_SEARCH_PLACEHOLDER = "Np. opis oferty, post na social media, wiadomość do klienta…";

function outputLabel(mode) {
  const m = (mode || "").toLowerCase();
  if (/(_edit|correction|comparison|testimonial_edit)/.test(m)) return "Edycja / korekta zdjęcia";
  if (/(portrait|visual|photo|shot_list|identity_audit)/.test(m)) return "Zdjęcie / wizualizacja AI";
  if (/(video|_script|speech|webinar)/.test(m)) return "Scenariusz / wideo";
  return "Gotowy tekst do skopiowania";
}

function PromptThumb({ src, name, Icon }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-elevated to-brand-bg">
        {Icon && <Icon size={34} className="text-brand-line" />}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
    />
  );
}

function toolIcon(tool) {
  return tool === "ChatGPT" || tool === "Canva" ? FileText : PenTool;
}

// Etykieta statusu zewnętrznego narzędzia AI (nie dotyczy ceny promptu w Promptivo).
const TOOL_STATUS_DISPLAY = { "Bezpłatny limit": "Darmowy plan narzędzia AI" };
const TOOL_STATUS_TOOLTIP =
  "Informacja dotyczy możliwości użycia gotowego promptu w bezpłatnym planie zewnętrznego narzędzia AI. Przygotowanie promptu w Promptivo kosztuje zgodnie z podaną liczbą kredytów.";

function ToolStatusBadge({ label }) {
  const display = TOOL_STATUS_DISPLAY[label] || label;
  const cls = "shrink-0 rounded-full bg-brand-elevated px-1.5 py-0.5 text-[10px] font-semibold text-brand-sec";
  if (label !== "Bezpłatny limit") return <span className={cls}>{display}</span>;
  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid="tool-status-info"
            aria-label={`${display} — wyjaśnienie`}
            onClick={(e) => e.preventDefault()}
            className={`${cls} cursor-help underline decoration-dotted underline-offset-2`}
          >
            {display}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] bg-brand-bg text-brand-text">
          <p className="text-xs leading-relaxed text-brand-sec">{TOOL_STATUS_TOOLTIP}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function PromptCard({ p, catName, isFavorite, onToggleFavorite, onOpen, saving }) {
  const Icon = CATEGORY_ICON_MAP[p.category_slug];
  const ToolIcon = toolIcon(p.tool_label);
  const rawThumb = p.thumbnail_src || p.category_thumbnail || null;
  const thumb = rawThumb ? `${rawThumb}${rawThumb.includes("?") ? "&" : "?"}v=4` : null;
  return (
    <article data-testid={LIBRARY.promptCard} className="group hover-glow flex flex-col overflow-hidden rounded-card border border-brand-line bg-brand-surface transition-transform duration-300 hover:-translate-y-1">
      <div className="relative aspect-video overflow-hidden">
        <PromptThumb src={thumb} name={p.name} Icon={Icon} />
        {p.is_new && (
          <span
            data-testid="prompt-new-badge"
            className="absolute left-2.5 top-2.5 inline-flex items-center gap-1 rounded bg-brand-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-on"
          >
            Nowość
          </span>
        )}
        <span className="absolute bottom-2.5 left-2.5 rounded bg-brand-accent px-2 py-0.5 text-[11px] font-semibold text-brand-on">
          {catName(p.category_slug)}
        </span>
        <button
          type="button"
          aria-label={isFavorite ? "Usuń z ulubionych" : "Dodaj do ulubionych"}
          title={isFavorite ? "W ulubionych" : "Dodaj do ulubionych"}
          data-testid={LIBRARY.favoriteToggle}
          disabled={saving}
          onClick={() => onToggleFavorite(p.slug)}
          className={`absolute right-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-brand-bg/70 backdrop-blur transition-colors disabled:opacity-60 ${
            isFavorite ? "text-brand-accent" : "text-white hover:text-brand-accent"
          }`}
        >
          <Heart size={16} strokeWidth={1.75} fill={isFavorite ? "currentColor" : "none"} />
        </button>
        <TooltipProvider delayDuration={120}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Co wygenerujesz w tym prompcie?"
                data-testid="prompt-info-tooltip"
                onClick={(e) => e.preventDefault()}
                className="absolute right-2.5 top-12 flex h-8 w-8 items-center justify-center rounded-full bg-brand-bg/70 text-white backdrop-blur transition-colors hover:text-brand-accent"
              >
                <Info size={16} strokeWidth={1.75} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[240px] bg-brand-bg text-brand-text">
              <p className="text-[11px] font-bold uppercase tracking-wide text-brand-accent">Co wygenerujesz</p>
              {p.short_benefit && <p className="mt-1 text-xs text-brand-sec">{p.short_benefit}</p>}
              <p className="mt-2 flex items-center gap-1 text-[11px] text-brand-muted">
                <Sparkles size={11} strokeWidth={2} className="text-brand-accent" /> Efekt: {outputLabel(p.mode)}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-[15px] font-semibold leading-snug text-brand-text">{p.name}</h3>
        <p className="mt-1 line-clamp-2 flex-1 text-[13px] text-brand-sec">{p.short_benefit}</p>
        <div className="mt-3 flex items-center justify-between gap-2">
          {p.tool_label ? (
            <span className="inline-flex min-w-0 items-center gap-1.5 text-xs text-brand-muted">
              <ToolIcon size={14} strokeWidth={1.75} className="shrink-0" />
              <span className="truncate">{p.tool_label}</span>
              {p.tool_status_label && <ToolStatusBadge label={p.tool_status_label} />}
            </span>
          ) : <span />}
          <button
            type="button"
            data-testid={LIBRARY.promptOpen}
            onClick={() => onOpen(p.slug)}
            className="inline-flex items-center gap-1.5 rounded-field bg-brand-accent px-3 py-1.5 text-xs font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
          >
            Otwórz prompt <ArrowRight size={13} strokeWidth={2} />
          </button>
        </div>
      </div>
    </article>
  );
}

function FullCta({ testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-start justify-between gap-4 rounded-panel border border-brand-line bg-brand-surface px-6 py-5 sm:flex-row sm:items-center">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-elevated text-brand-accent">
          <Sparkles size={18} strokeWidth={1.75} />
        </span>
        <div>
          <h3 className="font-display text-lg font-semibold text-brand-text">Pełny dostęp dla każdego</h3>
          <p className="text-sm text-brand-sec">Przeglądaj wszystkie prompty bez ograniczeń. Kredyty wykorzystujesz dopiero przy funkcjach AI.</p>
        </div>
      </div>
      <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-accent/10 px-4 py-2 text-sm font-semibold text-brand-accent">
        Kredyty nie wygasają
      </span>
    </div>
  );
}

export default function LibraryPackage() {
  const { slug, category } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [favorites, setFavorites] = useState({});
  const [favSaving, setFavSaving] = useState({});
  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    let active = true;
    setStatus("loading");
    libApi
      .package(slug)
      .then((d) => {
        if (!active) return;
        setData(d);
        const fav = {};
        (d.prompts || []).forEach((p) => (fav[p.slug] = p.is_favorite));
        setFavorites(fav);
        setStatus("ok");
      })
      .catch((err) => active && setStatus(err.response?.status === 403 ? "forbidden" : "error"));
    return () => {
      active = false;
    };
  }, [slug]);

  const activeCategory = category || null;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeCategory, query]);

  const catName = (cslug) => data?.categories.find((c) => c.slug === cslug)?.name || cslug;

  const searchResults = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return data.prompts.filter((p) => {
      const hay = [
        p.name,
        p.short_benefit,
        catName(p.category_slug),
        p.category_slug,
        p.mode,
        p.tool_label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [data, query]);

  const isSearching = query.trim().length > 0;

  const categoryPrompts = useMemo(() => {
    if (!data || !activeCategory) return [];
    return data.prompts.filter((p) => p.category_slug === activeCategory).slice(0, CATEGORY_LIMIT);
  }, [data, activeCategory]);

  const featured = useMemo(() => (data ? data.prompts.slice(0, FEATURED_COUNT) : []), [data]);
  const allPrompts = useMemo(() => (data ? data.prompts : []), [data]);

  const toggleFavorite = async (promptSlug) => {
    if (favSaving[promptSlug]) return; // blokada wielokrotnego kliknięcia podczas zapisu
    const current = favorites[promptSlug];
    setFavSaving((s) => ({ ...s, [promptSlug]: true }));
    setFavorites((f) => ({ ...f, [promptSlug]: !current }));
    try {
      if (current) await libApi.removeFavorite(promptSlug);
      else await libApi.addFavorite(promptSlug);
      toast.success(current ? "Usunięto z ulubionych" : "Dodano do ulubionych");
    } catch (err) {
      setFavorites((f) => ({ ...f, [promptSlug]: current }));
      toast.error(apiError(err.response?.data?.detail, "Nie udało się zaktualizować ulubionych."), {
        action: { label: "Spróbuj ponownie", onClick: () => toggleFavorite(promptSlug) },
      });
    } finally {
      setFavSaving((s) => { const n = { ...s }; delete n[promptSlug]; return n; });
    }
  };

  const openPrompt = (promptSlug) => navigate(`/prompt/${promptSlug}`);

  const cardProps = (p) => ({
    p,
    catName,
    isFavorite: !!favorites[p.slug],
    onToggleFavorite: toggleFavorite,
    saving: !!favSaving[p.slug],
    onOpen: openPrompt,
  });

  if (status === "loading") {
    return (
      <AppShell>
        <div className="mx-auto max-w-content">
          <div className="h-10 w-64 animate-pulse rounded bg-brand-surface" />
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square animate-pulse rounded-card border border-brand-line bg-brand-surface" />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  if (status === "forbidden") {
    return (
      <AppShell>
        <div className="mx-auto max-w-content rounded-panel border border-brand-line bg-brand-surface px-6 py-10">
          <h1 className="font-display text-2xl font-semibold text-brand-text">Ten pakiet nie jest jeszcze dostępny na Twoim koncie</h1>
          <p className="mt-2 max-w-lg text-brand-sec">Cały katalog jest bezpłatny po zalogowaniu. Wróć do biblioteki, aby przeglądać dostępne prompty.</p>
          <Link to="/biblioteka" className="mt-5 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-3 text-sm font-semibold text-brand-on">
            Wróć do biblioteki <ArrowRight size={16} strokeWidth={2} />
          </Link>
        </div>
      </AppShell>
    );
  }

  if (status === "error" || !data) {
    return (
      <AppShell>
        <div className="mx-auto max-w-content rounded-card border border-red-500/40 bg-red-500/10 px-5 py-6 text-sm text-red-300">
          <p>Nie udało się wczytać pakietu. Sprawdź połączenie i spróbuj ponownie.</p>
          <button
            type="button"
            data-testid="library-package-retry"
            onClick={() => window.location.reload()}
            className="mt-3 inline-flex items-center gap-2 rounded-button bg-brand-accent px-4 py-2 text-sm font-semibold text-brand-on"
          >
            Spróbuj ponownie
          </button>
        </div>
      </AppShell>
    );
  }

  const visibleAll = allPrompts.slice(0, visibleCount);
  const hasMore = visibleCount < allPrompts.length;

  return (
    <AppShell>
      <div className="mx-auto max-w-content" data-testid={LIBRARY.root}>
        <nav className="mb-4 flex items-center gap-2 text-sm text-brand-muted" data-testid={LIBRARY.backLink}>
          <Link to="/biblioteka" className="transition-colors hover:text-brand-text">Moja biblioteka</Link>
          <span>/</span>
          <span className="text-brand-sec">{data.name}</span>
        </nav>

        <h1 className="font-display text-3xl font-semibold text-brand-text sm:text-4xl">Co chcesz dziś stworzyć?</h1>
        <p className="mt-2 text-brand-sec">Wybierz cel albo wyszukaj gotowy prompt.</p>

        {/* Search */}
        <div className="relative mt-6">
          <Search size={18} strokeWidth={1.75} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid={LIBRARY.searchInput}
            placeholder={SEARCH_PLACEHOLDER[slug] || DEFAULT_SEARCH_PLACEHOLDER}
            className="h-12 w-full rounded-field border border-brand-line bg-brand-surface pl-12 pr-4 text-[15px] text-brand-text placeholder:text-brand-muted outline-none transition-colors focus:border-brand-accent/60"
          />
        </div>

        {/* Status bar */}
        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-2 rounded-card border border-brand-line bg-brand-surface px-5 py-3.5 text-sm">
          <span className="inline-flex items-center gap-2 font-semibold text-brand-accent">
            <STATUS_ICONS.active size={17} /> Pakiet aktywny
          </span>
          <span className="inline-flex items-center gap-2 text-brand-sec">
            <Package size={15} strokeWidth={1.75} className="text-brand-muted" /> {data.prompts.length} promptów
          </span>
          {data.new_count > 0 && (
            <span
              data-testid="package-new-count"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-accent px-2.5 py-1 text-xs font-bold text-brand-on"
            >
              <Sparkles size={13} strokeWidth={2} /> {data.new_count} nowych / zaktualizowanych
            </span>
          )}
          <span className="hidden items-center gap-2 text-brand-muted md:inline-flex">
            <STATUS_ICONS.updates size={16} className="text-brand-muted" /> Aktualizacje katalogu na bieżąco
          </span>
          {data.updated_display && (
            <span className="inline-flex items-center gap-2 text-brand-muted">
              <STATUS_ICONS.lastUpdate size={16} className="text-brand-muted" /> Ostatnia aktualizacja: {data.updated_display}
            </span>
          )}
        </div>

        {/* Category tiles */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8" data-testid={LIBRARY.categoryFilter}>
          {data.categories.map((c) => {
            const Icon = CATEGORY_ICON_MAP[c.slug] || (() => null);
            const active = activeCategory === c.slug;
            return (
              <button
                key={c.slug}
                type="button"
                onClick={() => navigate(active ? `/biblioteka/${slug}` : `/biblioteka/${slug}/${c.slug}`)}
                data-testid={`category-tile-${c.slug}`}
                className={`flex aspect-square flex-col items-center justify-center gap-2.5 rounded-card border px-2 text-center transition-all duration-200 ${
                  active
                    ? "border-brand-accent bg-brand-accent/10 text-brand-text"
                    : "border-brand-line bg-brand-surface text-brand-sec hover:border-brand-line/80 hover:text-brand-text"
                }`}
              >
                {c.icon_src ? (
                  <img src={c.icon_src} alt="" aria-hidden="true" loading="lazy" className="h-7 w-7" />
                ) : (
                  <Icon size={28} className="text-brand-accent" />
                )}
                <span className="text-[13px] font-medium leading-tight">{c.name}</span>
              </button>
            );
          })}
        </div>

        {/* ---------- SEARCH RESULTS ---------- */}
        {isSearching ? (
          <section className="mt-8" data-testid={LIBRARY.searchResults}>
            <div className="flex items-baseline justify-between gap-4">
              <h2 className="font-display text-xl font-semibold text-brand-text">Wyniki wyszukiwania</h2>
              <span className="text-sm text-brand-muted">{searchResults.length} promptów</span>
            </div>
            {searchResults.length === 0 ? (
              <div className="mt-5 flex flex-col items-start gap-4 rounded-card border border-brand-line bg-brand-surface px-5 py-8 text-brand-sec">
                <p>Nie znaleźliśmy promptu dla „{query.trim()}". Spróbuj innej frazy lub dopasuj prompt do celu.</p>
                <button
                  type="button"
                  data-testid="search-match-prompt-cta"
                  onClick={() => navigate(`/dopasuj-prompt?pkg=${data.slug}`)}
                  className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
                >
                  Dopasuj prompt
                </button>
              </div>
            ) : (
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {searchResults.map((p) => (
                  <PromptCard key={p.slug} {...cardProps(p)} />
                ))}
              </div>
            )}
          </section>
        ) : activeCategory ? (
          /* ---------- SINGLE CATEGORY (max 6) ---------- */
          <section className="mt-8">
            <div className="flex items-baseline justify-between gap-4">
              <h2 className="font-display text-xl font-semibold text-brand-text">{catName(activeCategory)}</h2>
              <span className="text-sm text-brand-muted">{categoryPrompts.length} promptów</span>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {categoryPrompts.map((p) => (
                <PromptCard key={p.slug} {...cardProps(p)} />
              ))}
            </div>
          </section>
        ) : (
          /* ---------- DEFAULT: FEATURED + ALL ---------- */
          <>
            <div className="mt-8 grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]" data-testid={LIBRARY.featuredSection}>
              <div className="min-w-0">
                <h2 className="font-display text-xl font-semibold text-brand-text">Polecane na początek</h2>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {featured.map((p) => (
                    <PromptCard key={p.slug} {...cardProps(p)} />
                  ))}
                </div>
              </div>

              {/* Help panel */}
              <aside className="xl:sticky xl:top-24 xl:self-start">
                <div className="flex flex-col items-center rounded-panel border border-brand-line bg-brand-surface px-6 py-8 text-center">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-accent/40 text-brand-accent">
                    <Sparkles size={20} strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-brand-text">Nie wiesz, od czego zacząć?</h3>
                  <p className="mt-2 text-sm text-brand-sec">Odpowiedz na kilka pytań, a dopasujemy najlepszy prompt do Twojego celu.</p>
                  <button
                    type="button"
                    data-testid="match-prompt-cta"
                    onClick={() => navigate(`/dopasuj-prompt?pkg=${data.slug}`)}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
                  >
                    Dopasuj prompt
                  </button>
                </div>
              </aside>
            </div>

            {/* CTA after featured */}
            <div className="mt-10">
              <FullCta testId={LIBRARY.fullCtaTop} />
            </div>

            {/* All prompts */}
            <section className="mt-10" data-testid={LIBRARY.allSection}>
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="font-display text-xl font-semibold text-brand-text">Wszystkie prompty</h2>
                <span className="text-sm text-brand-muted">{allPrompts.length} promptów</span>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {visibleAll.map((p) => (
                  <PromptCard key={p.slug} {...cardProps(p)} />
                ))}
              </div>
              {hasMore && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    data-testid={LIBRARY.showMoreButton}
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 rounded-button border border-brand-line px-6 py-3 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                  >
                    <Plus size={16} strokeWidth={2} /> Pokaż więcej ({allPrompts.length - visibleCount})
                  </button>
                </div>
              )}
            </section>
          </>
        )}

        {/* Bottom CTA (always) */}
        <div className="mt-12">
          <FullCta testId={LIBRARY.fullCtaBottom} />
        </div>
      </div>
    </AppShell>
  );
}
